'use client';

import * as React from 'react';

import { FlySvgSprite } from '@fly/svg';
import { Arrow } from '@radix-ui/react-popover';
import { Popover as RadixPopover, Separator } from '@radix-ui/themes';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';

import type {
  PopoverContentProps,
  PopoverRootProps,
  SimplePopoverProps,
} from './Popover.types';

/** Delay before closing on pointer leave so the user can move across the trigger–content gap (see HoverCard). */
const POPOVER_HOVER_CLOSE_DELAY_MS = 100;

const PopoverHoverContext = React.createContext<{
  onHoverOpen: () => void;
  onHoverLeave: () => void;
} | null>(null);

/**
 * Triangle sprite (see `triangle.svg`) reads `--fly-icon-triangle-fill` / `--fly-icon-triangle-stroke`;
 * values match `popoverSurface` bg and border.
 */
const popoverArrow = cva('icon block', {
  variants: {
    color: { accent: '', neutral: '' },
    variant: { soft: '', classic: '' },
  },
  compoundVariants: [
    {
      color: 'accent',
      variant: 'soft',
      class:
        '[--fly-icon-triangle-fill:var(--indigo-9)] [--fly-icon-triangle-stroke:color-mix(in_srgb,var(--indigo-10)_40%,transparent)]',
    },
    {
      color: 'accent',
      variant: 'classic',
      class:
        '[--fly-icon-triangle-fill:theme(colors.blue.500)] [--fly-icon-triangle-stroke:var(--indigo-11)]',
    },
    {
      color: 'neutral',
      variant: 'soft',
      class:
        '[--fly-icon-triangle-fill:var(--sand-4)]  [--fly-icon-triangle-stroke:var(--gray-6)]',
    },
    {
      color: 'neutral',
      variant: 'classic',
      class:
        '[--fly-icon-triangle-fill:var(--mauve-2)] [--fly-icon-triangle-stroke:var(--mauve-4)]',
    },
  ],
  defaultVariants: { color: 'neutral', variant: 'soft' },
});

const popoverSurface = cva(
  // `!` beats Themes' Popover panel overflow (arrow sits outside the border box).
  'relative box-border max-w-[332px] min-w-[100px] !overflow-visible rounded-fly-3 text-left',
  {
    variants: {
      color: { accent: '', neutral: '' },
      variant: { soft: '', classic: '' },
    },
    compoundVariants: [
      {
        color: 'accent',
        variant: 'soft',
        class:
          'border border-indigo-10/40 bg-[var(--indigo-9)] text-slate-1 shadow-[0_0_24px_0_rgba(0,0,0,0.04)]',
      },
      {
        color: 'accent',
        variant: 'classic',
        class:
          'border border-blue-600 bg-blue-500 text-slate-1 shadow-[0_0_24px_0_rgba(0,0,0,0.04)]',
      },
      {
        color: 'neutral',
        variant: 'soft',
        class:
          'border !border-gray-6 bg-[var(--sand-4)] text-slate-12 shadow-[0_0_24px_0_rgba(0,0,0,0.04)]',
      },
      {
        color: 'neutral',
        variant: 'classic',
        class:
          'border border-[var(--mauve-4)] bg-[var(--mauve-2)] text-slate-12 shadow-[0_0_24px_0_rgba(0,0,0,0.04)]',
      },
    ],
    defaultVariants: { color: 'neutral', variant: 'soft' },
  }
);

/**
 * Full-bleed with `Popover.Content`’s `!p-3` (0.75rem sides).
 * Radix `Separator` sets `width: 100%` on the 1px line, so *margin-only* pull (`-mx-3`)
 * shifts the line but does not widen the painted line — the right end stays short. The
 * wrapper is `w-[calc(100%+1.5rem)]` and `-mx-3` so the line actually spans the padded row.
 */
function PopoverSectionSeparator() {
  return (
    <div className='-mx-2 min-w-0 w-[calc(100%+1rem)] max-w-none shrink-0 self-stretch'>
      <Separator
        className='min-w-0 w-full max-w-none'
        decorative={false}
        size='4'
      />
    </div>
  );
}

/**
 * Hover-open popover: delayed close on leave so the pointer can cross the trigger–content gap.
 * Split from `PopoverRoot` so hook order stays valid (no hooks after `openOnHover` branch return).
 */
function PopoverRootWithHover({
  open: openProp,
  onOpenChange,
  defaultOpen,
  modal,
  children,
  openOnHover: _openOnHover,
  ...rest
}: PopoverRootProps) {
  const isControlled = openProp !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    () => defaultOpen ?? false
  );

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  const currentOpen = isControlled ? openProp! : uncontrolledOpen;
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const onHoverOpen = React.useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpen(true);
  }, [setOpen]);

  const onHoverLeave = React.useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      setOpen(false);
    }, POPOVER_HOVER_CLOSE_DELAY_MS);
  }, [setOpen]);

  React.useEffect(
    () => () => {
      if (closeTimeoutRef.current !== null) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    []
  );

  const hoverContext = React.useMemo(
    () => ({ onHoverOpen, onHoverLeave }),
    [onHoverOpen, onHoverLeave]
  );

  return (
    <PopoverHoverContext.Provider value={hoverContext}>
      <RadixPopover.Root
        open={currentOpen}
        onOpenChange={setOpen}
        modal={modal ?? false}
        {...rest}
      >
        {children}
      </RadixPopover.Root>
    </PopoverHoverContext.Provider>
  );
}

function PopoverRoot({
  openOnHover = false,
  open: openProp,
  onOpenChange,
  defaultOpen,
  modal,
  children,
  ...rest
}: PopoverRootProps) {
  if (!openOnHover) {
    const rootStateProps =
      openProp !== undefined
        ? { open: openProp }
        : defaultOpen !== undefined
          ? { defaultOpen }
          : {};

    return (
      <RadixPopover.Root
        {...rootStateProps}
        onOpenChange={onOpenChange}
        modal={modal}
        {...rest}
      >
        {children}
      </RadixPopover.Root>
    );
  }

  return (
    <PopoverRootWithHover
      open={openProp}
      onOpenChange={onOpenChange}
      defaultOpen={defaultOpen}
      modal={modal}
      {...rest}
    >
      {children}
    </PopoverRootWithHover>
  );
}

PopoverRoot.displayName = 'PopoverRoot';

function PopoverTrigger({
  onMouseEnter,
  onMouseLeave,
  ...rest
}: React.ComponentPropsWithRef<typeof RadixPopover.Trigger>) {
  const hover = React.useContext(PopoverHoverContext);
  return (
    <RadixPopover.Trigger
      onMouseEnter={(e) => {
        hover?.onHoverOpen();
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        hover?.onHoverLeave();
        onMouseLeave?.(e);
      }}
      {...rest}
    />
  );
}

PopoverTrigger.displayName = 'PopoverTrigger';

function PopoverContent({
  position = 'bottom',
  variant = 'soft',
  color = 'neutral',
  header,
  body,
  footer,
  children,
  insetContent = false,
  arrow = true,
  className,
  align = 'center',
  sideOffset,
  onMouseEnter,
  onMouseLeave,
  ...rest
}: PopoverContentProps) {
  const hover = React.useContext(PopoverHoverContext);
  const hasSectionProps = header != null || body != null || footer != null;
  const effectiveBody =
    body ?? (header != null || footer != null ? children : undefined);
  const ruleAfterHeader =
    (header != null && effectiveBody != null) ||
    (header != null && footer != null && effectiveBody == null);
  const ruleBeforeFooter = effectiveBody != null && footer != null;

  const inner = !hasSectionProps ? (
    (children ?? null)
  ) : insetContent ? (
    <div className='min-w-0 flex flex-col'>
      {header != null && (
        <div
          className={classNames(
            'min-w-0 flex flex-col gap-fly-3',
            (effectiveBody != null || footer != null) && 'pb-fly-4'
          )}
        >
          {header}
        </div>
      )}
      {ruleAfterHeader && <PopoverSectionSeparator />}
      {effectiveBody != null && (
        <div
          className={classNames(
            'min-w-0 flex flex-1 flex-col overflow-x-clip',
            ruleBeforeFooter && 'pb-fly-3',
            ruleAfterHeader && 'pt-fly-3'
          )}
        >
          {effectiveBody}
        </div>
      )}
      {ruleBeforeFooter && <PopoverSectionSeparator />}
      {footer != null && (
        <div
          className={classNames(
            'min-w-0',
            (header != null || effectiveBody != null) && 'pt-fly-3'
          )}
        >
          {footer}
        </div>
      )}
    </div>
  ) : (
    <div className='min-w-0 flex flex-col gap-fly-3'>
      {header != null && header}
      {effectiveBody != null && effectiveBody}
      {footer != null && footer}
    </div>
  );

  return (
    <RadixPopover.Content
      side={position}
      align={align}
      sideOffset={sideOffset !== undefined ? sideOffset : 8}
      onMouseEnter={(e) => {
        hover?.onHoverOpen();
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        hover?.onHoverLeave();
        onMouseLeave?.(e);
      }}
      {...rest}
      className={classNames(
        popoverSurface({ color, variant }),
        className,
        'px-fly-3 pt-fly-4! pb-fly-3'
      )}
    >
      {inner}
      {/* Radix Arrow viewBox is 30x10; triangle sprite is 16x8 — x=7, y=1 centers it. */}
      {arrow && (
        <Arrow
          asChild
          aria-hidden
          className='block shrink-0'
          height={8}
          width={24}
        >
          <FlySvgSprite
            className={classNames(popoverArrow({ color, variant }))}
            height={8}
            name='triangle'
            width={24}
          />
        </Arrow>
      )}
    </RadixPopover.Content>
  );
}

PopoverContent.displayName = 'PopoverContent';

const Popover = {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Close: RadixPopover.Close,
  Anchor: RadixPopover.Anchor,
  Content: PopoverContent,
} as const;

function SimplePopover({
  trigger,
  triggerProps,
  rootProps,
  contentProps,
}: SimplePopoverProps) {
  return (
    <Popover.Root {...rootProps}>
      <Popover.Trigger {...triggerProps}>{trigger}</Popover.Trigger>
      <Popover.Content {...contentProps} />
    </Popover.Root>
  );
}

export { Popover, PopoverContent, SimplePopover };
